import { PayloadAction, PayloadActionCreator } from "@logicsoftware/redux-toolkit";
import { ActionMatchingPattern, Task } from "@redux-saga/types";
import { Action } from "redux";
import { Buffer, buffers, Channel, channel } from "redux-saga";
import { ActionPattern, call, fork, take, takeLeading } from "redux-saga/effects";

type UpdateRequestsParams<P extends ActionPattern> = {
    pattern: P;
    groupKey?: (action: P extends PayloadActionCreator<infer T> ? PayloadAction<T> : any) => string | number;
    worker: (action: ActionMatchingPattern<P>) => any;
    /**
     * Indicates that all queued actions should processed.
     * By default watcher process only last queued action.
     *
     * For example if a1, a2, a3 are dispatched.
     * In this case by default will be processed only a1 and a3 actions.
     * But when this flag is set then a1, a2, a3 will be processed.
     */
    processAllRequests?: boolean;
};

type OperationInfo = {
    task?: Task;
    channel: Channel<Action>;
    buffer: Buffer<Action>;
    disposeIfNoPendingActions: () => void;
};

/**
 *  Makes sure that there won't be race conditions during save operations.
 *  Puts all actions in queue and handles them one by one.
 *
 *  By default handles only the last queued action when there multiple actions were queued.
 *  See processAllRequests option for details
 */
export const watchUpdateRequests = <P extends ActionPattern>({
    pattern,
    groupKey,
    worker,
    processAllRequests,
}: UpdateRequestsParams<P>) =>
    fork(function* () {
        const operations: Dictionary<OperationInfo> = {};

        groupKey ??= () => "all";

        while (true) {
            const action: Action = yield take(pattern);
            const key = groupKey(action as any);

            const operation = (operations[key] ||= createOperationInfo(key, operations, processAllRequests));

            if (!operation.task) {
                operation.task = yield takeLeading(
                    operation.channel,
                    function* (operation: OperationInfo, action) {
                        yield call(worker, action);
                        operation.disposeIfNoPendingActions();
                    },
                    operation
                );
            }

            operation.channel.put(action);
        }
    });

const createOperationInfo = (
    key: string | number,
    operations: Dictionary<OperationInfo>,
    processAllRequests?: boolean
) => {
    const buffer: Buffer<Action> = processAllRequests ? buffers.expanding() : buffers.sliding<Action>(1); // handle only the last pending action and skip intermediate.

    const operation: OperationInfo = {
        buffer,
        channel: channel(buffer),
        disposeIfNoPendingActions: () => {
            // free memory and stops takeLeading effect if there
            // is no more actions.
            if (buffer.isEmpty()) {
                operation.channel.close();
                delete operations[key];
            }
        },
    };
    return (operations[key] = operation);
};
